import React, {useEffect, useState} from "react";
import { Link } from "react-router-dom";
import LocalizedText from "../presentational/LocalizedText";
import { Button, FormGroup, Input, InputGroup, Intent } from "@careevolution/design-system";
import useTranslate from "../../hooks/useTranslate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

interface KnownUserLoginParameters {
  serverError: string;
  isLoading: boolean;
  emailOrPhone: string;
  notYouLink: string;
  forgotPassword: () => void;
  finishLogin: (password: string) => void;
  setEmailOrPhone: (emailOrPhone: string) => void;
  setRememberMe: (rememberMe: boolean) => void;
}

const KnownUserLogin: React.FC<KnownUserLoginParameters> = ({
  serverError,
  isLoading,
  emailOrPhone,
  notYouLink,
  forgotPassword,
  finishLogin,
  setEmailOrPhone,
  setRememberMe,
}) => {
  const [password, setPassword] = useState('')
  const [unmaskPassword, setUnmaskPassword] = useState(false)
  const translations = useTranslate(["participant-login-password-label", "participant-login-password-placeholder", "participant-login-next-label",])

  const handleChangeEmailOrPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailOrPhone(e.target.value)
  }

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const handleNotYouClick = () => {
    setEmailOrPhone('')
    setRememberMe(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    finishLogin(password)
    return false
  }

  useEffect(() => {
    if (window.renderTurnstile) {
      window.renderTurnstile();
    }
  }, []);

  return (
    <form className="login-action" onSubmit={handleSubmit}>
      <p className="personalized-welcome-back">
        <span className="personalized-welcome-back-title"><LocalizedText textKey="participant-login-login-password-title" /></span>
        <span className="personalized-welcome-back-userid"> {emailOrPhone}</span>
      </p>
      <Link className="not-you-link" to={notYouLink} onClick={handleNotYouClick}><LocalizedText textKey="participant-login-not-you-label" /></Link>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      <input type="text" id="username" value={emailOrPhone} onChange={handleChangeEmailOrPhone} onInput={handleChangeEmailOrPhone} autoComplete="username" hidden />
      <FormGroup
        className="login-field"
        label={translations["participant-login-password-label"]}
      >
        <InputGroup>

          <Input
            autoComplete="current-password"
            className="autofocus"
            disabled={isLoading}
            id="password"
            type={unmaskPassword ? 'text' : 'password'}
            value={password}
            placeholder={translations["participant-login-password-placeholder"]}
            fullWidth={true}
            onChange={handleChangePassword}
            htmlAttributes={{
              onInput: handleChangePassword,
              spellCheck: false,
              autoCorrect: "off",
              autoCapitalize: "none",
              maxLength: 64,
            }}
          />
          <button
            type="button"
            onClick={() => setUnmaskPassword(!unmaskPassword)}
            className="password-mask-toggle"
            title="Toggle password visibility">
               <FontAwesomeIcon icon={unmaskPassword ? faEye as IconProp: faEyeSlash as IconProp} />
          </button>
        </InputGroup>
      </FormGroup>
      <div className="turnstile-container">
        <div id="cf-turnstile" className="cf-turnstile"></div>
      </div>
      <Button
        className="primary-action"
        disabled={!password.length}
        onClick={handleSubmit}
        intent={Intent.primary}
        loading={isLoading}
        width='100%'
        type='submit'
      >
        <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
      </Button>
      <Button
        className="forgot-password-button"
        onClick={forgotPassword}
        width='100%'
        intent={Intent.secondary}
        variant='minimal'
      >
        <LocalizedText textKey='participant-login-forgot-password-label' />
      </Button>
    </form>
  )
}

export default KnownUserLogin